import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { DeclineDocumentVerificationModal } from './DeclineDocumentVerificationModal';
import apiService from '../../services/api';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface SingleGuardianVerificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    task: any;
    token: string;
    onSave: (data: { action: 'approve' | 'decline'; reason?: string; message?: string }) => Promise<void>;
    isSaving?: boolean;
}

interface PatientFile {
    id: number;
    created_at: number;
    patient_id: number;
    user_id: number;
    description: string;
    filename: string;
    file_type: string;
    patient_file_type_id: number;
    file_id: string;
}

export const SingleGuardianVerificationModal: React.FC<SingleGuardianVerificationModalProps> = ({
    isOpen,
    onClose,
    task,
    token,
    onSave,
    isSaving = false
}) => {
    const [error, setError] = useState<string | null>(null);
    const [patientFiles, setPatientFiles] = useState<PatientFile[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [selectedFile, setSelectedFile] = useState<PatientFile | null>(null);
    const [fileImageUrl, setFileImageUrl] = useState<string | null>(null);
    const [fileMimeType, setFileMimeType] = useState<string | null>(null);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState<'approve' | 'decline' | null>(null);
    const [showDeclineModal, setShowDeclineModal] = useState(false);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setFileImageUrl(null);
            setPatientFiles([]);
            setError(null);
            setShowConfirmation(false);
            setPendingAction(null);
            setShowDeclineModal(false);
        }
    }, [isOpen]);

    // Fetch patient files when modal opens
    useEffect(() => {
        const fetchFiles = async () => {
            if (!isOpen || !task?.id || !token) return;

            setIsLoadingFiles(true);
            try {
                const filesData = await apiService.getPatientFiles({
                    token,
                    patient_id: task.id
                });

                // Filter only file types 3 and 18
                const filteredFiles = filesData.filter((file: any) =>
                    file.patient_file_type_id === 3 || file.patient_file_type_id === 18
                );

                setPatientFiles(filteredFiles as any);
            } catch (err: any) {
                console.error("Failed to load patient files:", err);
            } finally {
                setIsLoadingFiles(false);
            }
        };

        fetchFiles();
    }, [isOpen, task?.id, token]);

    // Auto-select first file when files are loaded
    useEffect(() => {
        if (patientFiles.length > 0 && !selectedFile) {
            setSelectedFile(patientFiles[0]);
        }
    }, [patientFiles, selectedFile]);

    // Fetch file image when a file is selected
    useEffect(() => {
        let objectUrl: string | null = null;

        const fetchFileImage = async () => {
            if (!selectedFile) {
                setFileImageUrl(null);
                setFileMimeType(null);
                return;
            }

            setIsLoadingImage(true);
            try {
                const { url, mimeType } = await apiService.getFileImage(selectedFile.file_id);
                objectUrl = url;
                setFileImageUrl(url);
                setFileMimeType(mimeType);
            } catch (err: any) {
                console.error("Failed to load file image:", err);
                setError(err.response?.status === 415 ? 'Unsupported file type, please download the file' : 'Failed to load file preview');
                setTimeout(() => setError(null), 3000);
                setFileImageUrl(null);
                setFileMimeType(null);
            } finally {
                setIsLoadingImage(false);
            }
        };

        fetchFileImage();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [selectedFile]);

    const handleAction = (action: 'approve' | 'decline') => {
        // For decline, show the decline modal directly (skip confirmation)
        if (action === 'decline') {
            setShowDeclineModal(true);
            return;
        }

        // For approve, show confirmation dialog
        setPendingAction(action);
        setShowConfirmation(true);
    };

    const handleConfirmAction = async () => {
        if (!pendingAction) return;

        setError(null);
        setShowConfirmation(false);

        // Process approve action
        try {
            await onSave({ action: pendingAction });
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save verification');
        } finally {
            setPendingAction(null);
        }
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
        setPendingAction(null);
    };

    const handleDeclineSubmit = async (reason: string, message: string) => {
        setError(null);
        try {
            await onSave({ action: 'decline', reason, message });
            setShowDeclineModal(false);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save verification');
            throw err; // Re-throw to let the decline modal handle it
        }
    };

    // Calculate age from date of birth
    const calculateAge = (dob: string | number) => {
        if (!dob) return '—';
        const birthDate = new Date(typeof dob === 'number' ? dob : dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Format date for display
    const formatDate = (timestamp: number) => {
        if (!timestamp) return '—';
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB');
    };

    // Format date for DoB
    const formatDoB = (date: string | number) => {
        if (!date) return '—';
        const d = new Date(typeof date === 'number' ? date : date);
        return d.toISOString().split('T')[0];
    };

    // Check if parent 2 has any data
    const hasParent2Data = task?.parent_2_name || task?.parent_2_gender || task?.parent_2_email || task?.parent_2_phone_number;

    const handleFileClick = (file: PatientFile) => {
        setSelectedFile(file);
    };

    return (
        <ModalWrapper modalId="SingleGuardianVerificationModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Single Guardian Verification"
            width="w-[70vw]"
        >
            <div className="flex gap-6 h-[70vh]">
                {/* Left Side - Files and Details */}
                <div className="flex flex-col w-[35%]">
                    {/* Section 1 - Uploaded Files */}
                    <div className="flex flex-col mb-4">
                        <h3 className="text-sm font-semibold text-[#366A75] mb-4">Uploaded Files</h3>
                        <div className="max-h-[220px] overflow-y-auto space-y-2 p-3 bg-[#EBF5F5] rounded-lg">
                            {isLoadingFiles ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75] mx-auto"></div>
                                    <p className="text-sm text-[#366A75] mt-2">Loading files...</p>
                                </div>
                            ) : patientFiles.length === 0 ? (
                                <div className="text-center py-8 text-[#366A75]">
                                    <svg className="w-12 h-12 mx-auto mb-3 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-sm">No files available</p>
                                </div>
                            ) : (
                                patientFiles.map((file) => (
                                    <div
                                        key={file.id}
                                        onClick={() => handleFileClick(file)}
                                        className={`bg-white rounded-xl p-3 cursor-pointer transition-all border border-gray-200 ${
                                            selectedFile?.id === file.id
                                                ? 'ring-2 ring-[#366A75] shadow-md'
                                                : 'hover:bg-[#EBF5F5]'
                                        }`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-semibold text-[#366A75] truncate">
                                                    {file.file_type || 'Document'}
                                                </span>
                                                <span className="text-xs text-[#366A75] ml-2 shrink-0">
                                                    {formatDate(file.created_at)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-[#366A75] truncate">
                                                {file.filename}{file.description ? ` - ${file.description}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Section 2 - Patient and Parent Details */}
                    <div className="flex flex-col flex-1 overflow-y-auto p-3 bg-[#EBF5F5] rounded-lg">
                        <div className="space-y-6">
                    {/* Patient Details Section */}
                    <div>
                        <h3 className="text-lg font-semibold text-[#366A75] mb-4">Patient Details</h3>
                        <div className="space-y-2">
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Full name</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.first_name && task?.last_name
                                        ? `${task.first_name} ${task.last_name}`
                                        : '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Patient ID</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.HM_ID || '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Gender</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.gender || '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">DoB</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.dob ? formatDoB(task.dob) : '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Age</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.dob ? calculateAge(task.dob) : '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Phone</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.main_phone || task?.phone || '—'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Parent 1 Section */}
                    <div>
                        <h3 className="text-lg font-semibold text-[#366A75] mb-4">Parent 1</h3>
                        <div className="space-y-2">
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Name</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.parent_1_name || '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Gender</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.parent_1_gender || '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">E-mail</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.parent_1_email || '—'}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Phone</label>
                                <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                    {task?.parent_1_phone_number || '—'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Parent 2 Section */}
                    <div>
                        <h3 className="text-lg font-semibold text-[#366A75] mb-4">Parent 2</h3>
                        {hasParent2Data ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Name</label>
                                    <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                        {task?.parent_2_name || '—'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Gender</label>
                                    <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                        {task?.parent_2_gender || '—'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">E-mail</label>
                                    <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                        {task?.parent_2_email || '—'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-bold text-[#366A75] w-24 flex-shrink-0">Phone</label>
                                    <div className="px-4 py-2 bg-white rounded-full border border-gray-200 text-sm text-[#366A75] flex-1">
                                        {task?.parent_2_phone_number || '—'}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-[#366A75] italic">
                                Parent 2 details were not provided by the patient
                            </p>
                        )}
                    </div>
                        </div>
                    </div>
                </div>

                {/* Section 3 - Right Side - File Preview */}
                <div className="flex flex-col w-[65%] border-l border-gray-200 pl-6">
                    <h3 className="text-sm font-semibold text-[#366A75] mb-4">File Preview</h3>
                    <div className="flex-1 flex items-center justify-center bg-[#EBF5F5] rounded-lg overflow-hidden">
                        {!selectedFile ? (
                            <div className="text-center text-[#366A75]">
                                <svg className="w-16 h-16 mx-auto mb-3 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                <p className="text-sm">Select a file to preview</p>
                            </div>
                        ) : isLoadingImage ? (
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#366A75] mx-auto"></div>
                                <p className="text-sm text-[#366A75] mt-3">Loading preview...</p>
                            </div>
                        ) : fileImageUrl ? (
                            <div className="w-full h-full overflow-auto">
                                {fileMimeType === 'application/pdf' ? (
                                    <iframe
                                        src={fileImageUrl}
                                        title={selectedFile.filename}
                                        className="w-full h-full border-0"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center p-4">
                                        <img
                                            src={fileImageUrl}
                                            alt={selectedFile.filename}
                                            className="max-w-full max-h-full object-contain"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center text-[#366A75]">
                                <svg className="w-16 h-16 mx-auto mb-3 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm">Unable to load preview</p>
                            </div>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mt-4">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-4 justify-end items-center">
                        <button
                            onClick={onClose}
                            disabled={isSaving}
                            className="px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none mr-auto"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => handleAction('decline')}
                            disabled={isSaving}
                            className="px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                        >
                            {isSaving ? 'Processing...' : 'Decline'}
                        </button>
                        <button
                            onClick={() => handleAction('approve')}
                            disabled={isSaving}
                            className="px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                        >
                            {isSaving ? 'Processing...' : 'Approve'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmation && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]"
                    onClick={handleCancelConfirmation}
                >
                    <div
                        className="bg-[#B4D9D4] rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold text-[#366A75] mb-4">Confirm Action</h3>
                        <p className="text-[#366A75] mb-6">
                            Are you sure you want to {pendingAction} this patient?
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={handleCancelConfirmation}
                                disabled={isSaving}
                                className={POPUP_STYLES.button}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmAction}
                                disabled={isSaving}
                                className={POPUP_STYLES.button}
                            >
                                {isSaving ? 'Processing...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Decline Document Verification Modal */}
            <DeclineDocumentVerificationModal
                isOpen={showDeclineModal}
                onClose={() => setShowDeclineModal(false)}
                onSubmit={handleDeclineSubmit}
                isSaving={isSaving}
            />
        </ModalWrapper>
    );
};
